// E-mail texts for blåtur: strike notices and the invitation for new members
// Built from the data in the app (rules, trips, organizers), then sent from the organizer's own e-mail program

import { tripTitle, tripYear, tripCover } from './tripHelpers';
import { APPLY_TO, FOUNDED, RULES_SUMMARY, TAGLINE } from './blaturRules';
import { STRIKES_TO_OUT } from './strikes';

const list = (names) => names.length > 1 ? `${names.slice(0, -1).join(', ')} og ${names[names.length - 1]}` : names.join('');

export const mailtoLink = ({ to = [], bcc = [], subject = '', body = '' }) => {
  const params = [
    bcc.length ? `bcc=${bcc.map(encodeURIComponent).join(',')}` : '',
    subject ? `subject=${encodeURIComponent(subject)}` : '',
    body ? `body=${encodeURIComponent(body)}` : '',
  ].filter(Boolean).join('&');
  return `mailto:${to.map(encodeURIComponent).join(',')}${params ? `?${params}` : ''}`;
};

// Notice to someone with strikes: which trips, why, and how to apply to be readmitted
export function strikeEmail({ member, status, trips, year, organizers, origin }) {
  const tripsByYear = new Map(trips.map(t => [tripYear(t), t]));
  const lines = (member.absences || [])
    .filter(a => a.strike && status.strikeYears.includes(a.year))
    .sort((a, b) => a.year - b.year)
    .map(a => {
      const trip = tripsByYear.get(a.year);
      return `- Blåtur ${a.year}${trip ? ` (${tripTitle(trip)})` : ''}: ${a.reason}`;
    });

  const out = status.out;
  const subject = out ? `Blåtur ${year}: Du står ikke på invitasjonslisten` : `Blåtur: Du har fått en strike`;
  const body = [
    `Hei ${member.name.trim()}!`,
    '',
    'Blåtur er en tradisjon vi tar på alvor. Allerede i reglementet fra 2017 står det at den som takker nei '
      + `${STRIKES_TO_OUT} turer på rad, ikke blir invitert igjen. Fra høsten 2026 praktiserer vi denne regelen.`,
    '',
    'Du er registrert med fravær som teller som strike fra:',
    ...lines,
    '',
    out
      ? `Det betyr at du ikke står på invitasjonslisten til blåtur ${year}.`
      : 'Uteblir du fra neste tur også, mister du invitasjonen.',
    '',
    ...(out ? [
      'Vil du tilbake? Døra er ikke stengt, men du må krype til korset.',
      `Send en skriftlig søknad til ${APPLY_TO}. ${FOUNDED} Fortell hvorfor du vil være med igjen. `
        + 'Blir søknaden godkjent, må du regne med en straff før du er tilbake i varmen.',
      '',
      `Du kan også søke direkte i appen: ${origin}/blatur, under «Invitasjonsliste».`,
      '',
    ] : [
      'Har du en god grunn vi ikke har hørt, si fra, så ser vi på det.',
      '',
    ]),
    'Hilsen',
    organizers.length ? `${list(organizers)}, arrangører av blåtur ${year}` : 'Blåtur',
  ].join('\n');

  return { subject, body };
}

// One e-mail (BCC) to everyone who is out: not invited to the coming trip, and how to apply to come back
export function outEmail({ year, organizers, origin }) {
  const subject = `Blåtur ${year}: Du står ikke på invitasjonslisten`;
  const body = [
    'Hei!',
    '',
    'Blåtur er en tradisjon vi tar på alvor. Allerede i reglementet fra 2017 står det at den som takker nei '
      + `${STRIKES_TO_OUT} turer på rad, ikke blir invitert igjen. Fra høsten 2026 praktiserer vi denne regelen.`,
    '',
    `Du har uteblitt fra de ${STRIKES_TO_OUT} siste turene, og står derfor ikke på invitasjonslisten til blåtur ${year}.`,
    '',
    'Men døra er ikke stengt. Vil du tilbake, må du krype til korset:',
    `Send en skriftlig søknad til ${APPLY_TO}. ${FOUNDED} Fortell hvorfor du vil være med igjen. `
      + 'Blir søknaden godkjent, må du regne med en straff før du er tilbake på invitasjonslisten.',
    '',
    `Du kan også søke direkte i appen: ${origin}/blatur, under «Invitasjonsliste».`,
    '',
    'Vi håper å se deg igjen!',
    '',
    'Hilsen',
    organizers.length ? `${list(organizers)}, arrangører av blåtur ${year}` : 'Blåtur',
  ].join('\n');
  return { subject, body };
}

const escapeHtml = (text) => String(text || '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const paragraphs = (text) => escapeHtml(text).split(/\n{2,}/)
  .map(p => `<p style="margin:0 0 14px">${p.replace(/\n/g, '<br>')}</p>`).join('');

// Invitation for new members, with the concept, the rules and some earlier trips with group photos
export function invitationEmail({ year, organizers, intro, featuredTrips, origin, allTrips }) {
  const subject = `Du er invitert til blåtur ${year} 🌍`;
  const tripCount = allTrips.length;
  const signature = organizers.length ? `${list(organizers)}, arrangører av blåtur ${year}` : 'Blåtur';

  const tripBlocks = featuredTrips.map(trip => {
    const cover = tripCover(trip);
    const description = (trip.description || '').trim();
    return `
      <tr><td style="padding:0 0 28px">
        ${cover ? `<img src="${escapeHtml(cover)}" alt="Gruppebilde ${escapeHtml(tripTitle(trip))}" width="560" style="width:100%;max-width:560px;border-radius:10px;display:block">` : ''}
        <h3 style="font-family:Georgia,serif;font-size:20px;margin:12px 0 4px">${tripYear(trip)}: ${escapeHtml(tripTitle(trip))}</h3>
        <p style="margin:0 0 6px;color:#666;font-size:14px">
          Arrangert av ${escapeHtml(list(trip.Vert || []))} · ${(trip.Deltagelse || []).length} deltakere
        </p>
        ${description ? `<p style="margin:0">${escapeHtml(description)}</p>` : ''}
      </td></tr>`;
  }).join('');

  const html = `
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;font-family:Helvetica,Arial,sans-serif;font-size:15px;line-height:1.55;color:#1a1a1a">
  <tr><td style="padding:0 0 8px">
    <h1 style="font-family:Georgia,serif;font-size:26px;margin:0 0 6px">Du er invitert til blåtur ${year} 🌍</h1>
    <p style="margin:0 0 20px;color:#4a7fb5;font-weight:bold">${escapeHtml(TAGLINE)}</p>
    ${paragraphs(intro)}
  </td></tr>
  <tr><td style="padding:8px 0 16px">
    <h2 style="font-family:Georgia,serif;font-size:20px;margin:0 0 8px">Slik fungerer blåtur</h2>
    <ul style="margin:0;padding-left:20px">${RULES_SUMMARY.map(r => `<li style="margin-bottom:6px">${escapeHtml(r)}</li>`).join('')}</ul>
  </td></tr>
  <tr><td style="padding:8px 0 16px">
    <h2 style="font-family:Georgia,serif;font-size:20px;margin:0 0 4px">Noen av turene så langt</h2>
    <p style="margin:0 0 16px;color:#666">${tripCount} turer siden 2017. Her er noen av dem:</p>
  </td></tr>
  ${tripBlocks}
  <tr><td style="padding:0 0 16px">
    <p style="margin:0 0 14px">Alle turene, bildene og reglementet finner du på <a href="${escapeHtml(origin)}/blatur" style="color:#4a7fb5">${escapeHtml(origin.replace(/^https?:\/\//, ''))}/blatur</a>.</p>
    <p style="margin:0 0 14px">Svar på denne e-posten om du er med, så hører du mer om datoer når de er satt.</p>
    <p style="margin:0">Hilsen<br>${escapeHtml(signature)}</p>
  </td></tr>
</table>`;

  const text = [
    `Du er invitert til blåtur ${year}!`,
    TAGLINE,
    '',
    intro,
    '',
    'Slik fungerer blåtur:',
    ...RULES_SUMMARY.map(r => `- ${r}`),
    '',
    `Noen av turene så langt (${tripCount} turer siden 2017):`,
    ...featuredTrips.map(t => `- ${tripYear(t)}: ${tripTitle(t)}, arrangert av ${list(t.Vert || [])}`),
    '',
    `Alle turene, bildene og reglementet: ${origin}/blatur`,
    '',
    'Svar på denne e-posten om du er med, så hører du mer om datoer når de er satt.',
    '',
    'Hilsen',
    signature,
  ].join('\n');

  return { subject, html, text };
}

export const DEFAULT_INVITATION_INTRO = 'Hei!\n\n'
  + 'Vi vil gjerne ha deg med på blåtur! Blåtur er en årlig tur for gjengen, der bare arrangørene vet hvor turen går. '
  + 'Resten av oss møter opp der vi blir bedt om, med bagasje og godt humør, og får vite destinasjonen underveis.\n\n'
  + `${FOUNDED} Siden den gang har vi vært i alt fra vinkjellere i Kroatia til lederhosen i München og roadtrip gjennom Toscana. `
  + 'Nå åpner vi for nye folk vi er glad i, og du er en av dem.';
